/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */
import { useEffect, useMemo, useReducer } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { Button, theme } from "@nordcloud/gnui";
import { PlanSortFields, SortOrder } from "~/generated/graphql";
import { Mode, Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableDateRange } from "~/components/SidebarWithMultiSelect/SelectableDateRange";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { getDateRangeSelectionsText } from "~/components/SidebarWithMultiSelect/utils";
import { dateFormat } from "~/constants";
import { useDisclosure, useQueryState } from "~/hooks";
import { useGetAllActions } from "~/hooks/useGetAllActions";
import {
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
  isNotEmpty,
  isNotNil,
} from "~/tools";
import { plansExcludeFilterMap } from "~/tools/filterStateMaps";
import { useGetPlans } from "~/views/plans/hooks";

import {
  QueryGetPlans,
  planStatusCheckboxes,
  scheduleOptionsCheckboxes,
} from "./constants";
import { preparePlansFilterBy } from "./planFilterBuilders";

type State = {
  startDate?: string;
  endDate?: string;
  planStatuses: string[];
  excludePlanStatuses: boolean;
  planActionIds: string[];
  excludePlanActionIds: boolean;
  scheduleOptions: string[];
  excludeScheduleOptions: boolean;
};
type QueryGetPlansState = QueryGetPlans & {
  page?: string | number;
  limit?: string | number;
};

function buildState(queryState: QueryGetPlans) {
  return {
    startDate: queryState.startDateUTC
      ? dayjs(queryState.startDateUTC).format(dateFormat.shortDate)
      : undefined,
    endDate: queryState.endDateUTC
      ? dayjs(queryState.endDateUTC).format(dateFormat.shortDate)
      : undefined,
    planStatuses: queryState.planStatuses,
    excludePlanStatuses: queryState.excludePlanStatuses,
    planActionIds: queryState.planActionIds,
    excludePlanActionIds: queryState.excludePlanActionIds,
    scheduleOptions: queryState.scheduleOptions,
    excludeScheduleOptions: queryState.excludeScheduleOptions,
  };
}

function buildQueryStateFromSidebarState(
  queryState: QueryGetPlansState,
  state: State
): QueryGetPlansState {
  return {
    ...queryState,
    startDateUTC: state.startDate
      ? dayjs(state.startDate).format(dateFormat.fullDate)
      : undefined,
    endDateUTC: state.endDate
      ? dayjs(state.endDate).endOf("day").format(dateFormat.fullDate)
      : undefined,
    planStatuses: state.planStatuses,
    excludePlanStatuses: state.excludePlanStatuses,
    planActionIds: state.planActionIds,
    excludePlanActionIds: state.excludePlanActionIds,
    scheduleOptions: state.scheduleOptions,
    excludeScheduleOptions: state.excludeScheduleOptions,
  };
}

// eslint-disable-next-line complexity
export function PlanFiltersSidebar({
  resultsCount = 0,
}: {
  resultsCount?: number;
}) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } = useQueryState<QueryGetPlans>();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);

  const appliedState = buildState(queryState);
  const hasPendingChanges = hasFilterChanges(
    state,
    appliedState,
    plansExcludeFilterMap
  );
  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );

  const { count: previewCount, loading: previewLoading } = useGetPlans({
    page: String(draftQueryState.page ?? "1"),
    limit: String(draftQueryState.limit ?? "20"),
    filter: {
      filterBy: preparePlansFilterBy(draftQueryState),
      phrase: isNotEmpty(draftQueryState.searchPhrase?.trim() || "")
        ? draftQueryState.searchPhrase
        : undefined,
    },
    sort: {
      field: draftQueryState.field ?? PlanSortFields.Name,
      order: draftQueryState.order ?? SortOrder.Asc,
    },
    basic: true,
  });
  const previewResultsCount = previewLoading ? undefined : previewCount;
  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading,
    previewResultsCount,
    appliedResultsCount: resultsCount,
    entityLabel: "plans",
  });

  const applyFilters = () => {
    const nextQueryState = buildQueryStateFromSidebarState(queryState, state);

    updateQueryState({
      ...nextQueryState,
      limit: String(nextQueryState.limit ?? queryState.limit ?? "20"),
      page: "1",
    });
    close();
  };

  const clearAllFilters = () => {
    updateState({
      startDate: undefined,
      endDate: undefined,
      planStatuses: [],
      excludePlanStatuses: false,
      planActionIds: [],
      excludePlanActionIds: false,
      scheduleOptions: [],
      excludeScheduleOptions: false,
    });
    updateQueryState({
      ...queryState,
      startDateUTC: undefined,
      endDateUTC: undefined,
      planStatuses: [],
      excludePlanStatuses: false,
      planActionIds: [],
      excludePlanActionIds: false,
      scheduleOptions: [],
      excludeScheduleOptions: false,
      page: "1",
    });
  };

  const clearDateCreatedFilter = () => {
    updateState({ startDate: undefined, endDate: undefined });
  };

  const clearPlanStatusesFilter = () => {
    updateState({
      planStatuses: [],
      excludePlanStatuses: false,
    });
  };

  const clearPlanActionIdsFilter = () => {
    updateState({
      planActionIds: [],
      excludePlanActionIds: false,
    });
  };

  const clearScheduleOptionsFilter = () => {
    updateState({
      scheduleOptions: [],
      excludeScheduleOptions: false,
    });
  };

  const filterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "plans",
    excludeFilterMap: plansExcludeFilterMap,
  });
  const helperText =
    previewFooterState.helperTextOverride ?? filterSidebarState.helperText;

  const { customActions, systemActions } = useGetAllActions();

  const eventActionCheckboxes = [
    ...systemActions.map((action) => ({
      value: action.id,
      label: action.name,
    })),
    ...customActions.map((action) => ({
      value: action.id,
      label: action.name,
      subLabel: "Custom Action",
    })),
  ];

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <FilterWrapper
            name="Creation Date"
            isSelected={!!state?.startDate && !!state?.endDate}
            selectedOptions={["startDate", "endDate"]}
            selectedText={getDateRangeSelectionsText(
              state.startDate,
              state.endDate
            )}
            onClear={clearDateCreatedFilter}
          >
            <SelectableDateRange
              selectedRange={{
                startDate: state.startDate,
                endDate: state.endDate,
              }}
              onRangeSelect={(range) => updateState(range)}
              label="Time range when plan was created"
              setDefaultEndDateToToday
            />
          </FilterWrapper>
          <FilterWrapper
            name="Status"
            isSelected={
              isNotNil(state.planStatuses) && isNotEmpty(state.planStatuses)
            }
            selectedOptions={state.planStatuses ?? []}
            onClear={clearPlanStatusesFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.planStatuses ?? []}
              selectableItems={planStatusCheckboxes}
              isExcludeSelected={state.excludePlanStatuses}
              onMultiSelect={(planStatuses) => updateState({ planStatuses })}
              onModeChange={(mode) =>
                updateState({ excludePlanStatuses: mode === Mode.EXCLUDES })
              }
            />
          </FilterWrapper>
          <FilterWrapper
            name="Included Actions"
            isSelected={
              isNotNil(state.planActionIds) && isNotEmpty(state.planActionIds)
            }
            selectedOptions={state.planActionIds ?? []}
            onClear={clearPlanActionIdsFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.planActionIds ?? []}
              selectableItems={eventActionCheckboxes}
              isExcludeSelected={state.excludePlanActionIds}
              onMultiSelect={(actionIds) =>
                updateState({ planActionIds: actionIds })
              }
              onModeChange={(mode) =>
                updateState({ excludePlanActionIds: mode === Mode.EXCLUDES })
              }
            />
          </FilterWrapper>
          <FilterWrapper
            name="Schedule Option"
            isSelected={
              isNotNil(state.scheduleOptions) &&
              isNotEmpty(state.scheduleOptions)
            }
            selectedOptions={state.scheduleOptions ?? []}
            onClear={clearScheduleOptionsFilter}
          >
            <SelectableListWithSearch
              allowModeChange
              selectedOptions={state.scheduleOptions ?? []}
              selectableItems={scheduleOptionsCheckboxes}
              isExcludeSelected={state.excludeScheduleOptions}
              onMultiSelect={(scheduleOptions) =>
                updateState({ scheduleOptions })
              }
              onModeChange={(mode) =>
                updateState({
                  excludeScheduleOptions: mode === Mode.EXCLUDES,
                })
              }
            />
          </FilterWrapper>
        </Sidebar>
      </When>
    </>
  );
}
